import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import SolDivider from '../components/ui/SolDivider';
import { ClubCard, EmptyState } from '../components/ui/Cards';
import { motion, AnimatePresence } from 'framer-motion';

export default function Clubs() {
  const { clubs, CAPITULOS, currentUser, addClub, suggestClub } = useApp();
  const [filterTipo, setFilterTipo] = useState('');
  
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [showSuggestClub, setShowSuggestClub] = useState(false);
  const [showSuggestSuccess, setShowSuggestSuccess] = useState(false);

  // Form States
  const [clubForm, setClubForm] = useState({
    nombre: '', descripcion: '', tipo: 'Mateada', capitulo_id: '', foto_portada: ''
  });

  const [suggestForm, setSuggestForm] = useState({
    nombre: '', descripcion: '', tipo: 'Mateada', capitulo_id: ''
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!CAPITULOS?.length) return;
    setClubForm((prev) => (
      CAPITULOS.some((c) => c.id === prev.capitulo_id)
        ? prev
        : { ...prev, capitulo_id: CAPITULOS[0].id }
    ));
    setSuggestForm((prev) => (
      CAPITULOS.some((c) => c.id === prev.capitulo_id)
        ? prev
        : { ...prev, capitulo_id: CAPITULOS[0].id }
    ));
  }, [CAPITULOS]);

  const filteredClubs = filterTipo ? clubs.filter(c => c.tipo === filterTipo) : clubs;

  const canCreate = ['admin', 'moderador', 'colaborador'].includes(currentUser?.role);

  const handleCreateClub = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      const coverUrl = clubForm.foto_portada || 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?q=80&w=800&auto=format&fit=crop';
      await addClub({ ...clubForm, foto_portada: coverUrl });
      setShowCreateClub(false);
      setClubForm({ nombre: '', descripcion: '', tipo: 'Mateada', capitulo_id: CAPITULOS[0]?.id || '', foto_portada: '' });
    } catch (err) {
      console.error('addClub failed:', err);
      setFormError(err.message || 'No se pudo crear el club');
    }
  };

  const handleSuggestClub = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      await suggestClub(suggestForm);
      setShowSuggestClub(false);
      setSuggestForm({ nombre: '', descripcion: '', tipo: 'Mateada', capitulo_id: CAPITULOS[0]?.id || '' });
      setShowSuggestSuccess(true);
      setTimeout(() => {
        setShowSuggestSuccess(false);
      }, 4000);
    } catch (err) {
      console.error('suggestClub failed:', err);
      setFormError(err.message || 'No se pudo enviar la sugerencia');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute -inset-x-10 -top-10 h-[250px] bg-[#F6B40E]/5 -z-10 rounded-b-[100px]" />
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="label-technical text-[#74ACDF] mb-2 block">Comunidades Recurrentes</span>
            <h1 className="text-4xl md:text-6xl font-editorial font-bold text-[#1B2A4E] leading-tight">
              Clubs del <span className="text-[#F6B40E] italic">Movimiento.</span>
            </h1>
            <p className="text-lg text-stone-500 mt-4 max-w-lg">
              Subgrupos locales donde mantenemos vivas nuestras tradiciones mientras hacemos negocios.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={() => setShowSuggestClub(true)} 
              className="bg-white text-[#1B2A4E] hover:bg-stone-50 border border-stone-200 px-6 py-3 rounded-xl font-bold shadow-sm transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              <SafeIcon name="Lightbulb" className="w-5 h-5 text-[#F6B40E]" /> Sugerir Club
            </button>
            
            {canCreate && (
              <button 
                onClick={() => setShowCreateClub(true)} 
                className="bg-[#1B2A4E] hover:bg-[#253969] text-white px-6 py-3 rounded-xl font-bold shadow-lg transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                <SafeIcon name="Plus" className="w-5 h-5" /> Crear Club
              </button>
            )}
          </div>
        </div>
      </div>

      <SolDivider />

      <div>
        {/* Filters */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex gap-2 bg-white p-1.5 rounded-xl border border-stone-200 w-fit">
            <button onClick={() => setFilterTipo('')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${filterTipo === '' ? 'bg-stone-100 text-[#1B2A4E]' : 'text-stone-500 hover:text-[#1B2A4E]'}`}>
              Todos
            </button>
            <button onClick={() => setFilterTipo('Mateada')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${filterTipo === 'Mateada' ? 'bg-[#74ACDF]/10 text-[#74ACDF]' : 'text-stone-500 hover:text-[#1B2A4E]'}`}>
              Mateadas
            </button>
            <button onClick={() => setFilterTipo('Asado')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${filterTipo === 'Asado' ? 'bg-red-50 text-red-500' : 'text-stone-500 hover:text-[#1B2A4E]'}`}>
              Asados
            </button>
          </div>
        </div>

        {/* Grid */}
        {filteredClubs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredClubs.map((club, idx) => (
              <motion.div key={club.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}>
                <ClubCard club={club} capituloName={CAPITULOS.find(c => c.id === club.capitulo_id)?.nombre} />
              </motion.div>
            ))}
          </div>
        ) : (
          <EmptyState 
            icon="Users" 
            title="No hay clubes todavía" 
            message={filterTipo ? `No encontramos clubes tipo "${filterTipo}".` : "Los clubes son espacios recurrentes de la comunidad."} 
          />
        )}
      </div>

      {/* Success Toast */}
      <AnimatePresence>
        {showSuggestSuccess && (
          <motion.div 
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 50, x: '-50%' }}
            className="fixed bottom-24 left-1/2 z-50 bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl font-medium flex items-center gap-3"
          >
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center shrink-0">
              <SafeIcon name="Check" className="w-5 h-5" />
            </div>
            Tu sugerencia fue enviada, un colaborador la va a revisar.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Club Modal (Admin/Collaborator only) */}
      <AnimatePresence>
        {showCreateClub && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCreateClub(false)} className="absolute inset-0 bg-[#1B2A4E]/60 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[32px] shadow-2xl w-full max-w-xl relative z-10 overflow-hidden border border-stone-100"
            >
              <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
                <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Crear un Club</h2>
                <p className="text-sm text-stone-500 mt-1">Un espacio para encuentros recurrentes en la comunidad.</p>
              </div>
              <form onSubmit={handleCreateClub} className="p-8 space-y-5">
                {formError && showCreateClub && (
                  <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{formError}</div>
                )}
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Nombre del Club</label>
                  <input required type="text" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" placeholder="Ej: Mateada de Miami" value={clubForm.nombre} onChange={e => setClubForm({...clubForm, nombre: e.target.value})} />
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Descripción</label>
                  <textarea required rows="3" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" placeholder="¿De qué trata este club?" value={clubForm.descripcion} onChange={e => setClubForm({...clubForm, descripcion: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block label-technical text-stone-500 mb-2">Tipo</label>
                    <select className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={clubForm.tipo} onChange={e => setClubForm({...clubForm, tipo: e.target.value})}>
                      <option>Mateada</option>
                      <option>Asado</option>
                      <option>Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block label-technical text-stone-500 mb-2">Capítulo</label>
                    <select required className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={clubForm.capitulo_id} onChange={e => setClubForm({...clubForm, capitulo_id: e.target.value})}>
                      {CAPITULOS.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">URL de Foto de Portada (Opcional)</label>
                  <input type="url" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-sm text-stone-500" placeholder="https://..." value={clubForm.foto_portada} onChange={e => setClubForm({...clubForm, foto_portada: e.target.value})} />
                </div>
                <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowCreateClub(false)} className="px-6 py-3 rounded-xl font-bold text-stone-500 hover:bg-stone-100 transition-colors">
                    Cancelar
                  </button>
                  <button type="submit" className="px-8 py-3 rounded-xl font-bold bg-[#1B2A4E] text-white hover:bg-[#253969] transition-colors shadow-md">
                    Crear Club
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Suggest Club Modal (All Members) */}
      <AnimatePresence>
        {showSuggestClub && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowSuggestClub(false)} className="absolute inset-0 bg-[#1B2A4E]/60 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[32px] shadow-2xl w-full max-w-xl relative z-10 overflow-hidden border border-stone-100"
            >
              <div className="p-8 border-b border-stone-100 bg-[#FAF9F6] flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F6B40E]/10 rounded-full flex items-center justify-center text-[#F6B40E]">
                  <SafeIcon name="Lightbulb" className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Sugerir un Club</h2>
                  <p className="text-sm text-stone-500 mt-1">Tu idea será revisada por el equipo para crear el club.</p>
                </div>
              </div>
              <form onSubmit={handleSuggestClub} className="p-8 space-y-5">
                {formError && showSuggestClub && (
                  <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{formError}</div>
                )}
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Nombre Sugerido</label>
                  <input required type="text" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" placeholder="Ej: Tech Drinks Austin" value={suggestForm.nombre} onChange={e => setSuggestForm({...suggestForm, nombre: e.target.value})} />
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Descripción e Idea</label>
                  <textarea required rows="3" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" placeholder="¿De qué trata este club y por qué lo sumamos?" value={suggestForm.descripcion} onChange={e => setSuggestForm({...suggestForm, descripcion: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block label-technical text-stone-500 mb-2">Tipo</label>
                    <select className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={suggestForm.tipo} onChange={e => setSuggestForm({...suggestForm, tipo: e.target.value})}>
                      <option>Mateada</option>
                      <option>Asado</option>
                      <option>Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block label-technical text-stone-500 mb-2">Capítulo</label>
                    <select required className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={suggestForm.capitulo_id} onChange={e => setSuggestForm({...suggestForm, capitulo_id: e.target.value})}>
                      {CAPITULOS.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                </div>
                <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowSuggestClub(false)} className="px-6 py-3 rounded-xl font-bold text-stone-500 hover:bg-stone-100 transition-colors">
                    Cancelar
                  </button>
                  <button type="submit" className="px-8 py-3 rounded-xl font-bold bg-[#1B2A4E] text-white hover:bg-[#253969] transition-colors shadow-md">
                    Enviar Sugerencia
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}